import { useEffect, useState, useCallback } from "react";
import { api, TASK_CLASS_LABELS, DAY_TYPE_LABELS, DAY_TYPE_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const CLASSES = ["mind", "snc", "drills", "nutrition", "physio", "medical"];
const DAY_TYPES = ["snc", "sports_skill", "travel", "match", "rest", "recovery"];

export default function TaskDashboards() {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [athleteId, setAthleteId] = useState("");
  const [active, setActive] = useState("mind");
  const [tasks, setTasks] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const isStaff = user?.role !== "athlete";
  const canMedical = ["coach", "physio"].includes(user?.role);

  useEffect(() => {
    api.get("/athletes").then((r) => { setAthletes(r.data); if (r.data[0]) setAthleteId(r.data[0].athlete_id); });
  }, []);

  const load = useCallback(() => {
    if (!athleteId) return;
    api.get(`/tasks?athlete_id=${athleteId}&task_class=${active}`).then((r) => setTasks(r.data));
  }, [athleteId, active]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  return (
    <div>
      <header className="min-h-16 border-b border-border flex flex-wrap items-center py-3 px-8 justify-between sticky top-0 bg-background z-10">
        <div className="flex items-center gap-4">
          <h1 className="font-heading text-xl font-black tracking-tight">Task Dashboards</h1>
          <select data-testid="athlete-select" value={athleteId} onChange={(e) => setAthleteId(e.target.value)} className="border border-border bg-background text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground">
            {athletes.map((a) => <option key={a.athlete_id} value={a.athlete_id}>{a.name}</option>)}
          </select>
        </div>
        {isStaff && (
          <button data-testid="open-add-task" onClick={() => setShowAdd(true)} className="bg-foreground text-background text-sm px-4 py-2 flex items-center gap-2 transition-colors hover:bg-neutral-800">
            <Plus className="w-4 h-4" /> Add Task
          </button>
        )}
      </header>

      <div className="flex border-b border-border overflow-x-auto">
        {CLASSES.map((c) => {
          const locked = c === "medical" && !canMedical;
          return (
            <button
              key={c}
              data-testid={`class-tab-${c}`}
              disabled={locked}
              onClick={() => setActive(c)}
              className={`px-5 py-3 text-sm whitespace-nowrap border-b-2 flex items-center gap-2 transition-colors ${
                active === c ? "border-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
              } ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {locked && <Lock className="w-3 h-3" />} {TASK_CLASS_LABELS[c]}
            </button>
          );
        })}
      </div>

      <div className="p-8">
        {tasks.length === 0 ? (
          <div className="border border-border p-10 text-center text-sm text-muted-foreground" data-testid="empty-tasks">No {TASK_CLASS_LABELS[active]} tasks yet.</div>
        ) : (
          <div className="border border-border divide-y divide-border" data-testid="task-list">
            {tasks.map((t) => (
              <div key={t.task_id} className="p-4 flex items-center gap-4" style={{ borderLeft: `3px solid ${DAY_TYPE_COLORS[t.day_type]}` }} data-testid={`task-${t.task_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(t.date)} · {t.duration_min}min · {t.intensity} {t.details && `· ${t.details}`}</div>
                </div>
                <span className="text-xs tracking-widest uppercase font-bold px-2 py-1 border border-border" style={{ color: DAY_TYPE_COLORS[t.day_type] }}>{DAY_TYPE_LABELS[t.day_type]}</span>
                {isStaff && <button data-testid={`del-task-${t.task_id}`} onClick={() => del(t.task_id)} className="text-muted-foreground hover:text-traffic-red transition-colors"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && <AddTask athleteId={athleteId} taskClass={active} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddTask({ athleteId, taskClass, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", day_type: "snc", date: new Date().toISOString().slice(0,10), duration_min: 60, intensity: "moderate", details: "" });
  const save = async () => {
    if (!f.title) return toast.error("Title required");
    try {
      await api.post("/tasks", { athlete_id: athleteId, task_class: taskClass, ...f, duration_min: Number(f.duration_min) });
      toast.success("Task added"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-6 z-50" onClick={onClose}>
      <div className="bg-background border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-task-modal">
        <div className="px-6 h-14 border-b border-border flex items-center font-heading font-bold tracking-tight">Add {TASK_CLASS_LABELS[taskClass]} Task</div>
        <div className="p-6 space-y-4">
          <Field label="Title"><input data-testid="task-title" value={f.title} onChange={set("title")} className="inp" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Day Type">
              <select data-testid="task-daytype" value={f.day_type} onChange={set("day_type")} className="inp">
                {DAY_TYPES.map((d) => <option key={d} value={d}>{DAY_TYPE_LABELS[d]}</option>)}
              </select>
            </Field>
            <Field label="Date"><input type="date" data-testid="task-date" value={f.date} onChange={set("date")} className="inp" /></Field>
            <Field label="Duration (min)"><input type="number" value={f.duration_min} onChange={set("duration_min")} className="inp" /></Field>
            <Field label="Intensity">
              <select value={f.intensity} onChange={set("intensity")} className="inp"><option>low</option><option>moderate</option><option>high</option></select>
            </Field>
          </div>
          <Field label="Details"><textarea value={f.details} onChange={set("details")} className="inp h-20" /></Field>
        </div>
        <div className="px-6 py-4 border-t border-border flex gap-3 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-border hover:bg-surface transition-colors">Cancel</button>
          <button data-testid="save-task" onClick={save} className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors">Save</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">{label}</span><div className="mt-1.5">{children}</div></label>
);
